/**
 * Animation Configuration
 * Defines parameters for different animation modes (preview, confirmation)
 */

export type AnimationTrigger = 'PAN_MOVE_PREVIEW' | 'PAN_END_SMOOTH' | 'DOUBLE_CLICK_ANIMATE' | null

export type EasingFunction = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'

export interface EffectsConfig {
  fadeIn?: {
    startOpacity: number
    endOpacity: number
  }
  fadeEnvelope?: {
    frameWindow: number  // Number of frames to spread cross-fade effect over (e.g., 15)
  }
  scale?: {
    startScale: number
    endScale: number
  }
  blur?: {
    startBlur: number
    endBlur: number
  }
}

export interface AnimationModeConfig {
  /** Duration per frame in milliseconds */
  frameDuration: number
  /** Sample every nth frame (1 = all frames, 2 = every 2nd frame) */
  frameSamplingRate: number
  /** Z-index for rendering the animation frame */
  zIndex: number
  /** Opacity of the animation frame (0-1) */
  opacity: number
  /** Border color for visual identification */
  borderColor: string
  /** Label for debugging */
  label: string
  /** Optional animation effects with timing */
  effects?: EffectsConfig
  /** Easing function for effects */
  easingFunction?: EasingFunction
}

/**
 * Configuration for double-click animation timing
 */
export interface DoubleClickTimingConfig {
  /** Duration per frame in milliseconds (may be adjusted for long sequences) */
  frameDuration: number
  /** Frames to animate (may skip frames for very long sequences) */
  framesToShow: number[]
  /** Total expected duration in milliseconds */
  totalDuration: number
}

/** Timing constants for double-click animation */
const DOUBLE_CLICK_TIMING = {
  /** Default ms per frame when sequence is short */
  baseDuration: 150,
  /** Minimum ms per frame (prevents blur) */
  minDuration: 40,
  /** Maximum total animation duration in ms */
  maxTotalDuration: 2000,
}

/**
 * Calculate optimal timing for frame sequence animation based on frame count
 * - Short sequences (≤26 frames): 150ms per frame
 * - Medium sequences (27-100 frames): Adjust duration to fit in 4s
 * - Long sequences (>100 frames): Skip frames to fit in 4s at 40ms minimum
 */
export function calculateFrameSequenceTiming(
  sourceFrame: number,
  targetFrame: number
): DoubleClickTimingConfig {
  const direction = targetFrame > sourceFrame ? 1 : -1
  const frameCount = Math.abs(targetFrame - sourceFrame)
  
  // Build all frames in sequence (excluding source, including target)
  const allFrames: number[] = []
  for (let i = sourceFrame + direction; direction > 0 ? i <= targetFrame : i >= targetFrame; i += direction) {
    allFrames.push(i)
  }
  
  // Case 1: Short sequence - use base duration
  if (frameCount <= 26) {
    return {
      frameDuration: DOUBLE_CLICK_TIMING.baseDuration,
      framesToShow: allFrames,
      totalDuration: frameCount * DOUBLE_CLICK_TIMING.baseDuration,
    }
  }
  
  // Case 2: Medium sequence - adjust duration to fit in max time
  if (frameCount <= 100) {
    const adjustedDuration = Math.floor(DOUBLE_CLICK_TIMING.maxTotalDuration / frameCount)
    return {
      frameDuration: Math.max(adjustedDuration, DOUBLE_CLICK_TIMING.minDuration),
      framesToShow: allFrames,
      totalDuration: frameCount * Math.max(adjustedDuration, DOUBLE_CLICK_TIMING.minDuration),
    }
  }
  
  // Case 3: Long sequence - skip frames to fit in max time at min duration
  // Calculate how many frames we can show at min duration within max time
  const maxFramesToShow = Math.floor(DOUBLE_CLICK_TIMING.maxTotalDuration / DOUBLE_CLICK_TIMING.minDuration)
  const skipRate = Math.ceil(frameCount / maxFramesToShow)
  
  const framesToShow: number[] = []
  for (let i = 0; i < allFrames.length; i += skipRate) {
    framesToShow.push(allFrames[i])
  }
  // Always include the target frame
  if (framesToShow[framesToShow.length - 1] !== targetFrame) {
    framesToShow.push(targetFrame)
  }
  
  return {
    frameDuration: DOUBLE_CLICK_TIMING.minDuration,
    framesToShow,
    totalDuration: framesToShow.length * DOUBLE_CLICK_TIMING.minDuration,
  }
}

export interface AnimationConfig {
  /** Preview animation during pan-move for real-time feedback */
  PAN_MOVE_PREVIEW: AnimationModeConfig
  /** Smooth animation on pan-end for confirmation */
  PAN_END_SMOOTH: AnimationModeConfig
  /** Animation for double-click/double-tap frame navigation */
  DOUBLE_CLICK_ANIMATE: AnimationModeConfig
}

/**
 * Global animation configuration
 * Customize these values to tune animation behavior
 */
export const ANIMATION_CONFIG: AnimationConfig = {
  PAN_MOVE_PREVIEW: {
    frameDuration: 100, // Increased from 75ms for visibility
    frameSamplingRate: 1, // Changed from 2 to show ALL frames
    zIndex: 25000, // Layer below final selection
    opacity: 0.8, // Increased from 0.6 for better visibility
    borderColor: '#FFFF00', // Yellow border for preview
    label: 'PREVIEW',
  },
  PAN_END_SMOOTH: {
    frameDuration: 150, // Slower confirmation: 150ms per frame
    frameSamplingRate: 1, // Show all frames for smooth confirmation
    zIndex: 40000, // Top layer
    opacity: 1.0, // Full opacity
    borderColor: '#FF00FF', // Magenta border for confirmation
    label: 'CONFIRM',
  },
  DOUBLE_CLICK_ANIMATE: {
    frameDuration: 150, // Base duration, dynamically adjusted by calculateFrameSequenceTiming
    frameSamplingRate: 1, // Show all frames (or skip via calculateFrameSequenceTiming)
    zIndex: 50000, // Highest priority
    opacity: 1.0, // Full opacity
    borderColor: '#00FFFF', // Cyan border for double-click navigation
    label: 'DOUBLE_CLICK',
  },
}

/**
 * Get animation config for a trigger type
 */
export function getAnimationConfig(trigger: AnimationTrigger): AnimationModeConfig | null {
  if (!trigger) return null
  return ANIMATION_CONFIG[trigger]
}

/**
 * Get animation label for debugging/logging
 */
export function getAnimationLabel(trigger: AnimationTrigger): string {
  if (!trigger) return 'NONE'
  const config = getAnimationConfig(trigger)
  return config?.label || 'UNKNOWN'
}
